import { useState, useEffect, useRef } from 'react';
import { Lock, ShieldAlert, Timer, Eye, EyeOff } from 'lucide-react';
import { LOGS_PIN_CODE } from '@/config/env';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const PIN_LENGTH = LOGS_PIN_CODE.length || 6;
const MAX_ATTEMPTS = 3;
const LOCKOUT_SECONDS = 60;

interface Props {
  open: boolean;
  onVerified: () => void;
  onCancel: () => void;
}

export function LogsPinDialog({ open, onVerified, onCancel }: Props) {
  const [digits,      setDigits]      = useState<string[]>(Array(PIN_LENGTH).fill(''));
  const [showPin,     setShowPin]     = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [attempts,    setAttempts]    = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const pin = digits.join('');

  // Countdown while locked
  useEffect(() => {
    if (!lockedUntil) return;
    const tick = () => {
      const left = Math.ceil((lockedUntil - Date.now()) / 1000);
      if (left <= 0) {
        setLockedUntil(null);
        setSecondsLeft(0);
        setAttempts(0);
        setError(null);
      } else {
        setSecondsLeft(left);
      }
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [lockedUntil]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setDigits(Array(PIN_LENGTH).fill(''));
      setError(null);
      setShowPin(false);
      setTimeout(() => inputRefs.current[0]?.focus(), 80);
    }
  }, [open]);

  const isLocked = !!lockedUntil && secondsLeft > 0;

  function handleVerify(currentPin = pin) {
    if (isLocked || currentPin.length < PIN_LENGTH) return;
    if (currentPin === LOGS_PIN_CODE) {
      setDigits(Array(PIN_LENGTH).fill(''));
      setError(null);
      setAttempts(0);
      onVerified();
    } else {
      const next = attempts + 1;
      setAttempts(next);
      setDigits(Array(PIN_LENGTH).fill(''));
      setTimeout(() => inputRefs.current[0]?.focus(), 30);
      if (next >= MAX_ATTEMPTS) {
        setLockedUntil(Date.now() + LOCKOUT_SECONDS * 1000);
        setSecondsLeft(LOCKOUT_SECONDS);
        setError(`Too many attempts. Locked for ${LOCKOUT_SECONDS}s.`);
      } else {
        const remaining = MAX_ATTEMPTS - next;
        setError(`Incorrect PIN. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`);
      }
    }
  }

  function handleDigitChange(index: number, value: string) {
    if (isLocked) return;
    // Handle paste of full PIN
    if (value.length > 1) {
      const pasted = value.replace(/\D/g, '').slice(0, PIN_LENGTH);
      const next = Array(PIN_LENGTH).fill('');
      pasted.split('').forEach((ch, i) => { next[i] = ch; });
      setDigits(next);
      setError(null);
      const focusIdx = Math.min(pasted.length, PIN_LENGTH - 1);
      setTimeout(() => inputRefs.current[focusIdx]?.focus(), 10);
      if (pasted.length === PIN_LENGTH) {
        setTimeout(() => handleVerify(pasted), 30);
      }
      return;
    }
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    setError(null);
    if (digit && index < PIN_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    if (digit && index === PIN_LENGTH - 1) {
      setTimeout(() => handleVerify(next.join('')), 30);
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const next = [...digits];
        next[index] = '';
        setDigits(next);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        const next = [...digits];
        next[index - 1] = '';
        setDigits(next);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < PIN_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleCancel() {
    setDigits(Array(PIN_LENGTH).fill(''));
    setError(null);
    onCancel();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleCancel(); }}>
      <DialogContent className="max-w-[360px] gap-0 overflow-hidden rounded-2xl border border-border/60 bg-card p-0 shadow-2xl">

        {/* Hero */}
        <div className="flex flex-col items-center px-6 pb-2 pt-4 text-center">
          <div className="relative mb-2 flex items-center justify-center">
            <span className="absolute h-16 w-16 animate-ping rounded-full bg-teal-500/10" style={{ animationDuration: '2.5s' }} />
            <span className="absolute h-14 w-14 rounded-full bg-teal-500/10 blur-sm" />
            <span className="absolute h-10 w-10 rounded-full bg-teal-500/20" />
            <span className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 shadow-lg shadow-teal-600/30">
              <Lock className="h-5 w-5 text-white" />
            </span>
          </div>
          <DialogTitle className="text-base font-bold tracking-tight text-foreground">
            Secure Area
          </DialogTitle>
          <DialogDescription className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
            Enter the access PIN to view attendance change logs.
          </DialogDescription>
        </div>

        {/* PIN boxes */}
        <div className="px-6 pb-1 pt-2">
          <div className="mb-1.5 flex items-center justify-between">
            <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground/70">
              Access PIN
            </label>
            <button
              type="button"
              aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
              onClick={() => setShowPin((v) => !v)}
              className="flex items-center gap-1 text-[10px] text-muted-foreground transition-colors hover:text-foreground"
            >
              {showPin ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              {showPin ? 'Hide' : 'Show'}
            </button>
          </div>

          <div className="flex gap-2">
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={(el) => { inputRefs.current[i] = el; }}
                type={showPin ? 'text' : 'password'}
                inputMode="numeric"
                maxLength={PIN_LENGTH}
                value={digit}
                disabled={isLocked}
                autoComplete="off"
                onChange={(e) => handleDigitChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onFocus={(e) => e.target.select()}
                className={cn(
                  'h-10 w-full min-w-0 rounded-xl border-2 bg-background text-center font-mono text-base font-semibold',
                  'transition-all duration-150 outline-none',
                  'placeholder:text-muted-foreground/20',
                  digit
                    ? error
                      ? 'border-destructive/60 bg-destructive/5 text-destructive'
                      : 'border-primary/60 bg-primary/5 text-foreground'
                    : 'border-border text-foreground',
                  'focus:border-primary focus:bg-primary/5 focus:shadow-[0_0_0_3px_hsl(var(--primary)/0.12)]',
                  error && digit ? 'animate-[shake_0.3s_ease]' : '',
                  isLocked ? 'cursor-not-allowed opacity-50' : '',
                )}
              />
            ))}
          </div>
        </div>

        {/* Error / lockout */}
        <div className="min-h-[1.75rem] px-6 py-1">
          {isLocked ? (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              <Timer className="h-3.5 w-3.5 shrink-0" />
              <span>Locked — try again in <strong>{secondsLeft}s</strong>.</span>
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-1.5 px-6 pb-4 pt-0">
          <Button
            onClick={() => handleVerify()}
            disabled={isLocked || pin.length < PIN_LENGTH}
            className="h-11 w-full gap-2 bg-teal-600 font-semibold text-white shadow-sm shadow-teal-600/20 hover:bg-teal-700 dark:bg-teal-600 dark:hover:bg-teal-500"
          >
            <Lock className="h-4 w-4" />
            Unlock Logs
          </Button>
          <Button
            variant="ghost"
            onClick={handleCancel}
            className="h-9 w-full text-muted-foreground hover:text-foreground"
          >
            Cancel
          </Button>
        </div>

      </DialogContent>
    </Dialog>
  );
}
